/**
 * Singleton API client for Homepage module
 *
 * Centralizes all API calls with proper typing.
 * Uses Infrastructure's request helper.
 */

import { request } from '../../../Infrastructure'
import { getCategoryUrls, getHomepageConfig, getHomepageReviews, getProductsByCategory, getProductsBySku } from '../queries'

import type { CategoryUrlsData, HomepageConfigResponse, HomepageReviewsResponse, ProductsData } from '../types'

/**
 * Homepage API client
 *
 * Note: request<T>() returns Promise<{ data: T }>, so we unwrap to return T directly
 */
export const homepageApi = {
  /**
   * Fetch homepage configuration (banners, sliders, promo blocks)
   */
  getConfig: async (): Promise<HomepageConfigResponse> => {
    const response = await request<HomepageConfigResponse>(getHomepageConfig, {})
    return response.data
  },

  /**
   * Fetch products by category ID
   */
  getProductsByCategory: async (categoryId: string, pageSize: number): Promise<ProductsData> => {
    const response = await request<ProductsData>(getProductsByCategory, { categoryId, pageSize })
    return response.data
  },

  /**
   * Fetch products by SKU list
   */
  getProductsBySkus: async (skus: string[], pageSize: number): Promise<ProductsData> => {
    const response = await request<ProductsData>(getProductsBySku, { skus, pageSize })
    return response.data
  },

  /**
   * Fetch category URLs by IDs (batch query)
   */
  getCategoryUrls: async (categoryIds: string[]): Promise<CategoryUrlsData> => {
    const response = await request<CategoryUrlsData>(getCategoryUrls, { categoryIds })
    return response.data
  },

  /**
   * Fetch homepage reviews (separate cache from homepageConfig)
   */
  getReviews: async (): Promise<HomepageReviewsResponse> => {
    const response = await request<HomepageReviewsResponse>(getHomepageReviews, {})
    return response.data
  },
}
